import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useAddDisapprovalReason from "@babackapp/private-api-client/client/use-add-disapproval-reason";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Check } from "lucide-react";
import { useState } from "react";

type Props = {
  /** Text to store as the reason's message, in the return's language. */
  message: string;
  lang: string;
  onSaved: (reasonId: string) => void;
  onCancel: () => void;
};

/**
 * Turns the free text just written into a reusable reason.
 *
 * Asks for a label rather than deriving a truncated one from the message: the
 * label is what the team reads in this very modal next time.
 */
export function SaveAsReasonForm({ message, lang, onSaved, onCancel }: Props) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutate: addReason, isPending } = useAddDisapprovalReason(shop);
  const [label, setLabel] = useState("");

  return (
    <div className="flex items-end gap-2 rounded-md bg-muted p-2">
      <div className="flex flex-1 flex-col gap-1">
        <label
          htmlFor="disapproval-new-reason-label"
          className="text-xs text-muted-foreground"
        >
          {t("disapproveReturn.saveAsReason.label")}
        </label>
        <Input
          id="disapproval-new-reason-label"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={100}
          autoFocus
        />
      </div>
      <Button
        type="button"
        size="sm"
        disabled={!label.trim() || isPending}
        onClick={() =>
          addReason(
            {
              label: label.trim(),
              help: null,
              message: [{ lang, text: message }],
            },
            { onSuccess: (reason) => onSaved(reason.id) },
          )
        }
      >
        <Check className="size-4" />
        {t("misc.save")}
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
        {t("misc.cancel")}
      </Button>
    </div>
  );
}
